import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient from '@/api/client'
import type { PnLReport } from '@/types'
import { getCategoryBadge } from './expense-constants'
import { formatDateHuman, formatDateTimeHuman } from '@/lib/utils'
import { useExpenseCategoryDefs } from './use-expense-category-defs'
import {
  DollarSign,
  ArrowUpCircle,
  ArrowDownCircle,
  Receipt,
  FileText,
  Calculator,
  ChevronRight,
  Info,
} from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { metricPairOrEmpty } from '@/components/admin/dashboard/dashboardFormat'

function formatPeriod(dateStr: string, period: string): string {
  try {
    if (period === 'hourly') return formatDateTimeHuman(dateStr)
    if (period === 'weekly') return `Week of ${formatDateHuman(dateStr)}`
    return formatDateHuman(dateStr)
  } catch {
    return dateStr
  }
}

type Props = {
  pnl?: PnLReport
  period: string
  setPeriod: (p: string) => void
  from: string
  setFrom: (f: string) => void
  to: string
  setTo: (t: string) => void
}

export function ExpensePnLTab({ pnl, period, setPeriod, from, setFrom, to, setTo }: Props) {
  const { formatCurrency } = useCurrency()
  const { data: defs = [] } = useExpenseCategoryDefs()

  // Pull projected gross margin for the same window via reports/v2/overview.
  // Kept as an independent query (not derived from PnLReport) because the
  // legacy /admin/reports/pnl endpoint uses tax-included revenue keyed off
  // completed_at, while reports/v2/overview uses net-of-tax sales keyed off
  // created_at. The reconciliation strip labels both clearly so the user can
  // compare them as-is rather than us silently fudging one to match.
  const projectedQuery = useQuery({
    enabled: Boolean(from && to),
    queryKey: ['reports-v2', 'overview', 'pnl-projected', from, to],
    queryFn: () => apiClient.getReportsOverview(from, to),
    staleTime: 30_000,
  })
  const projected = projectedQuery.data?.data

  const chartData =
    pnl?.rows?.map(r => ({
      label: formatPeriod(r.period, period),
      revenue: r.revenue,
      expenses: r.expenses,
      net: r.net_profit,
    })) ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Granularity</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hourly">Hourly</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">From</Label>
          <DatePicker className="w-[180px]" value={from} onChange={setFrom} max={to || undefined} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">To</Label>
          <DatePicker className="w-[180px]" value={to} onChange={setTo} min={from || undefined} />
        </div>
        <p className="max-w-md text-xs text-muted-foreground">
          Revenue uses <strong>completed order</strong> dates (aligned with daily closing). Expenses use expense date.
        </p>
      </div>

      {/* Projected vs Actual reconciliation strip — sits ABOVE the existing
          actuals summary so the eye reads:
            Net Sales → Projected COGS → Projected Margin (assumed)
            Net Sales → Tracked Expenses → Net Profit (actual)
          The two rows are intentionally laid out symmetrically. The Info
          tooltip warns about the double-counting trap when users record
          ingredient purchases as expenses AND set per-item projected costs. */}
      {projected && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calculator className="h-4 w-4 text-violet-500" />
              Projected vs actual profitability
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground">
                      <Info className="h-4 w-4" aria-label="What's the difference?" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm space-y-2 text-xs">
                    <p>
                      <strong>Projected gross margin</strong> uses your menu costing
                      assumptions (set under Manage Catalog → Costing) — net sales minus
                      qty&times;projected_cost for items that have a cost set.
                    </p>
                    <p>
                      <strong>Net profit</strong> uses your actual expense ledger
                      — total revenue minus tracked expenses, including non-product
                      costs like rent, salaries, utilities.
                    </p>
                    <p className="text-amber-600 dark:text-amber-400">
                      Heads-up: if you also record ingredient purchases as expenses,
                      both rows already include a form of cost. Treat them as two
                      independent estimates rather than adding them together.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-3 text-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Projected (assumed)
              </span>
              <span className="text-foreground">Net sales</span>
              <span className="font-semibold tabular-nums">{formatCurrency(projected.net_sales.current)}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="text-foreground">− Projected COGS</span>
              <span className="font-semibold tabular-nums text-violet-600 dark:text-violet-400">
                {formatCurrency(metricPairOrEmpty(projected.projected_cogs).current)}
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="text-foreground">= Projected margin</span>
              <span
                className={`font-bold tabular-nums ${
                  metricPairOrEmpty(projected.projected_margin).current >= 0
                    ? 'text-emerald-600'
                    : 'text-destructive'
                }`}
              >
                {formatCurrency(metricPairOrEmpty(projected.projected_margin).current)}
              </span>
              <Badge
                variant="outline"
                className={
                  metricPairOrEmpty(projected.projected_coverage_pct).current < 50
                    ? 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300'
                    : ''
                }
              >
                {metricPairOrEmpty(projected.projected_coverage_pct).current.toFixed(0)}% covered
              </Badge>
            </div>
            {pnl?.summary && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-3 text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Actual (ledger)
                  </span>
                  <span className="text-foreground">Revenue</span>
                  <span className="font-semibold tabular-nums">{formatCurrency(pnl.summary.total_revenue)}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <span className="text-foreground">− Tracked expenses</span>
                  <span className="font-semibold tabular-nums text-destructive">
                    {formatCurrency(pnl.summary.total_expenses)}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <span className="text-foreground">= Net profit</span>
                  <span
                    className={`font-bold tabular-nums ${
                      pnl.summary.net_profit >= 0 ? 'text-emerald-600' : 'text-destructive'
                    }`}
                  >
                    {formatCurrency(pnl.summary.net_profit)}
                  </span>
                </div>
                {/*
                  F-RPT-N1: refund-adjusted line. Only render when refunds
                  in the period are non-zero — keeps the clean-day view
                  uncluttered. The "Real take-home" copy intentionally
                  avoids "Net profit" so the operator can see at a glance
                  this is a different number than the green tile above.
                */}
                {(pnl.summary.refunds_issued ?? 0) > 0.005 && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-rose-200 bg-rose-50/60 p-3 text-sm dark:border-rose-900 dark:bg-rose-950/30">
                    <span className="text-xs font-medium uppercase tracking-wide text-rose-700 dark:text-rose-300">
                      After refunds
                    </span>
                    <span className="text-foreground">Net profit</span>
                    <span className="font-semibold tabular-nums">{formatCurrency(pnl.summary.net_profit)}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <span className="text-foreground">− Refunds issued</span>
                    <span className="font-semibold tabular-nums text-rose-700 dark:text-rose-300">
                      {formatCurrency(pnl.summary.refunds_issued ?? 0)}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <span className="text-foreground">= Real take-home</span>
                    <span
                      className={`font-bold tabular-nums ${
                        (pnl.summary.net_profit_after_refunds ?? pnl.summary.net_profit) >= 0
                          ? 'text-emerald-600'
                          : 'text-destructive'
                      }`}
                    >
                      {formatCurrency(
                        pnl.summary.net_profit_after_refunds ?? pnl.summary.net_profit - (pnl.summary.refunds_issued ?? 0),
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {pnl?.summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[
            {
              label: 'Total revenue',
              value: pnl.summary.total_revenue,
              color: 'text-emerald-600',
              icon: <ArrowUpCircle className="h-5 w-5 text-emerald-500" />,
              cur: true,
            },
            {
              label: 'Total tax',
              value: pnl.summary.total_tax,
              color: 'text-muted-foreground',
              icon: <FileText className="h-5 w-5" />,
              cur: true,
            },
            {
              label: 'Total orders',
              value: pnl.summary.total_orders,
              color: 'text-blue-600',
              icon: <Receipt className="h-5 w-5 text-blue-500" />,
              cur: false,
            },
            {
              label: 'Total expenses',
              value: pnl.summary.total_expenses,
              color: 'text-destructive',
              icon: <ArrowDownCircle className="h-5 w-5 text-destructive" />,
              cur: true,
            },
            {
              label: 'Net profit',
              value: pnl.summary.net_profit,
              color: pnl.summary.net_profit >= 0 ? 'text-emerald-600' : 'text-destructive',
              icon: <DollarSign className="h-5 w-5 text-violet-500" />,
              cur: true,
            },
          ].map(s => (
            <Card key={s.label}>
              <CardContent className="p-4">
                <div className="mb-1 flex items-center gap-2">
                  {s.icon}
                  <span className="text-xs text-muted-foreground">{s.label}</span>
                </div>
                <p className={`text-xl font-bold tabular-nums ${s.color}`}>
                  {s.cur ? formatCurrency(Number(s.value)) : s.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {chartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Revenue, expenses &amp; net</CardTitle>
          </CardHeader>
          <CardContent className="h-[320px] pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(Number(v))} width={72} />
                <ReTooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
                <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="net" name="Net" stroke="#8b5cf6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Period breakdown</CardTitle>
            </CardHeader>
            <div className="overflow-x-auto px-6 pb-6">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="p-3 text-left font-medium text-muted-foreground">Period</th>
                    <th className="p-3 text-right font-medium text-muted-foreground">Revenue</th>
                    <th className="p-3 text-right font-medium text-muted-foreground">Orders</th>
                    <th className="p-3 text-right font-medium text-muted-foreground">Expenses</th>
                    <th className="p-3 text-right font-medium text-muted-foreground">Net profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pnl?.rows && pnl.rows.length > 0 ? (
                    pnl.rows.map((r, i) => (
                      <tr key={i} className="hover:bg-muted/40">
                        <td className="p-3 text-muted-foreground">{formatPeriod(r.period, period)}</td>
                        <td className="p-3 text-right font-medium text-emerald-600 tabular-nums">
                          {formatCurrency(r.revenue)}
                        </td>
                        <td className="p-3 text-right tabular-nums text-muted-foreground">{r.orders}</td>
                        <td className="p-3 text-right font-medium text-destructive tabular-nums">
                          {formatCurrency(r.expenses)}
                        </td>
                        <td
                          className={`p-3 text-right font-bold tabular-nums ${
                            r.net_profit >= 0 ? 'text-emerald-600' : 'text-destructive'
                          }`}
                        >
                          {formatCurrency(r.net_profit)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="p-10 text-center text-muted-foreground">
                        No data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Expense by category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pnl?.expense_breakdown && pnl.expense_breakdown.length > 0 ? (
              pnl.expense_breakdown.map(eb => {
                const badge = getCategoryBadge(eb.category, defs)
                return (
                  <div key={eb.category} className="flex items-center justify-between rounded-lg bg-muted/50 p-2">
                    <Badge className={badge.color}>{badge.label}</Badge>
                    <span className="font-semibold tabular-nums">{formatCurrency(eb.total)}</span>
                  </div>
                )
              })
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">No expenses in this period</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
